import { DateTime, Interval } from "luxon";
import { VerifyMethodType } from "shared/types";
import { ReasonCode } from "types/landlord/reports";
import {
  PaystubInscribeAdditionalData,
  PaystubItem
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/paystub";

const getRejectionChecks = (
  additionalData?: PaystubInscribeAdditionalData | null
) => [
  {
    condition: additionalData?.type_mismatch,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "The document is not a paystub"
    }
  },
  {
    condition: additionalData?.is_fraudulent,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "Fraudulent activity detected"
    }
  },
  {
    condition: additionalData?.high_risk_level,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "High risk level"
    }
  },
  {
    condition: additionalData?.low_trust_score,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "Low trust score"
    }
  },
  {
    condition: additionalData?.low_quality_score,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "The file is of poor quality"
    }
  },
  {
    condition: additionalData?.missing_period,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "Pay period is missing"
    }
  },
  {
    condition: !additionalData?.is_name_present,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "No employee name present on document"
    }
  },
  {
    condition: !additionalData?.is_employer_name_present,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "No employer name present on document"
    }
  },
  {
    condition: additionalData?.no_amounts_extracted,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "No amounts could be extracted from the document"
    }
  }
];

export const getPaystubReasonCodes = (
  additionalData?: PaystubInscribeAdditionalData
): ReasonCode[] => {
  if (!additionalData) return [];

  const warnings: ReasonCode[] = [];
  const checks = getRejectionChecks(additionalData);
  checks.forEach(({ condition, reasonCode }) => {
    if (condition) warnings.push(reasonCode);
  });

  return warnings;
};

const differenceInDays = (startDate = "", endDate = "") => {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  if (!start.isValid || !end.isValid) return 0;

  const days = Interval.fromDateTimes(start, end).length("days");
  return Math.round(days);
};

const PAY_FREQUENCIES = [
  { label: "Weekly", days: 7 },
  { label: "Bi-weekly", days: 14 },
  { label: "Semi-monthly", days: 15 },
  { label: "Monthly", days: 30 }
];

export const estimatePaystubPayPeriod = (startDate = "", endDate = "") => {
  const dayCount = differenceInDays(startDate, endDate);
  // If the day count is somehow larger than a month, don't assume it's monthly
  if (!dayCount || dayCount > 32) return "Unknown";

  // Find the pay frequency closest to the given paystub dates
  const frequency = PAY_FREQUENCIES.reduce((closest, current) => {
    if (Math.abs(closest.days - dayCount) < Math.abs(current.days - dayCount))
      return closest;
    return current;
  }, PAY_FREQUENCIES[0]);
  return frequency.label;
};

export const createInscribePaystubRows = (items: PaystubItem[]) =>
  items.flatMap(
    (item) =>
      item.data?.document?.income_sources?.flatMap(
        (incSource) =>
          incSource.pay_stubs?.map((paystub) => ({
            ...paystub,
            isInscribe: true,
            verifyType: VerifyMethodType.Paystub,
            sourceStatus: item.status,
            additional_data: item.income_source_data?.additional_data,
            name_mismatch:
              item.income_source_data?.additional_data?.name_mismatch,
            trust_score: item.income_source_data?.raw_data?.trust_score
          })) ?? []
      ) ?? []
  );
